<template>
  <v-dialog v-model="open" max-width="720" persistent scrollable>
    <v-card>
      <v-card-title class="kf-dialog__title">
        {{ dialogTitle }}
      </v-card-title>
      <v-divider />

      <!-- ------------------------------------------------------- the form -->
      <v-card-text v-if="!generated" class="pt-4">
        <v-form ref="form" v-model="form_valid">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.name"
                label="Campaign name *"
                :rules="$rules.REQURIED_FILED"
                dense
                outlined
              />
            </v-col>
            <!-- Locked once the campaign exists: the id is signed into the
                 token, so a new one would be a new link and the one already
                 shared would stop working. Shown rather than hidden -- it is
                 what the marketing team reports against. -->
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.campaign_id"
                label="Campaign id *"
                :rules="editing ? [] : campaign_id_rules"
                :readonly="!!editing"
                :filled="!!editing"
                :hint="
                  editing
                    ? 'Cannot be changed — links already shared use this id.'
                    : 'Typed by you. Letters, numbers, - and _ only. Must be unique.'
                "
                persistent-hint
                dense
                outlined
              />
            </v-col>
          </v-row>

          <!-- Where the partner link sends a visitor. Website is what every
               campaign did before the link was tracked, so it stays the
               default; App and Smart are what carry a click into an install. -->
          <v-row dense class="mt-1">
            <v-col cols="12">
              <v-select
                v-model="form.destination"
                :items="destination_items"
                label="Link opens"
                :hint="destinationHint"
                persistent-hint
                dense
                outlined
              />
            </v-col>
          </v-row>

          <!-- Only when editing: a new campaign is always created active, and
               the row menu is the other way to reach this same switch. -->
          <v-row v-if="editing" dense class="mt-1">
            <v-col cols="12">
              <v-switch
                v-model="form.is_active"
                :label="
                  form.is_active
                    ? 'Active — the link shows these products'
                    : 'Inactive — the link says the campaign is no longer active'
                "
                class="mt-0 kf-switch"
                dense
                hide-details
                inset
              />
            </v-col>
          </v-row>
        </v-form>

        <div class="kf-section">
          Products
          <span class="kf-section__count">{{ selected.length }} selected</span>
        </div>

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="search"
              label="Search products"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
              @input="debouncedLoad"
            />
          </v-col>
          <v-col cols="6" sm="3">
            <v-select
              v-model="filter_status"
              :items="status_items"
              label="Status"
              dense
              outlined
              hide-details
              clearable
              @change="load"
            />
          </v-col>
          <v-col cols="6" sm="3">
            <v-select
              v-model="filter_recommended"
              :items="recommended_items"
              label="Recommended"
              dense
              outlined
              hide-details
              clearable
              @change="load"
            />
          </v-col>
        </v-row>

        <div class="kf-picker">
          <div v-if="loading" class="kf-picker__state">Loading products…</div>
          <div v-else-if="load_error" class="kf-picker__state kf-picker__state--error">
            {{ load_error }}
            <v-btn x-small text class="text-none" @click="load">Retry</v-btn>
          </div>
          <div v-else-if="!products.length" class="kf-picker__state">
            No products match these filters.
          </div>
          <template v-else>
            <div class="kf-picker__bar">
              <v-btn x-small text class="text-none" @click="toggleAllOnPage">
                {{ allOnPageSelected ? "Clear these" : "Select all" }}
              </v-btn>
              <!-- Ticks survive a filter change, so an admin can search "Fibe",
                   tick it, search "Kredit", tick that, and generate with both.
                   Without this the count would silently drop back as they typed. -->
              <span class="kf-picker__note">
                Ticks are kept when you change the filters.
              </span>
            </div>
            <div
              v-for="product in products"
              :key="product.id"
              class="kf-row"
              @click="toggle(product.id)"
            >
              <v-simple-checkbox
                :value="isSelected(product.id)"
                :ripple="false"
                dense
                @input="toggle(product.id)"
              />
              <div class="kf-logo">
                <img v-if="product.logo" :src="product.logo" alt="" />
                <span v-else>—</span>
              </div>
              <div class="kf-row__main">
                <div class="kf-row__name">{{ product.name }}</div>
                <div class="kf-row__sub">{{ product.product_type || "—" }}</div>
              </div>
              <span v-if="product.flag_recomended" class="kf-tag kf-tag--rec">
                Recommended
              </span>
              <span
                class="kf-tag"
                :class="product.is_active ? 'kf-tag--on' : 'kf-tag--off'"
              >
                {{ product.is_active ? "Active" : "Inactive" }}
              </span>
            </div>
          </template>
        </div>

        <!-- Selected-but-hidden is the confusing case: the count says 3 and the
             list shows one tick, because the other two are behind the filter. -->
        <div v-if="hiddenSelectedCount" class="kf-hidden-note">
          {{ hiddenSelectedCount }} more selected product{{
            hiddenSelectedCount === 1 ? " is" : "s are"
          }}
          hidden by the current filters.
        </div>

        <!-- The link already exists when editing, so it can be handed out from
             here too -- not only from the "URL ready" step after creating. -->
        <CampaignPartnerLink v-if="editing && editing.dynamic_url" :url="editing.dynamic_url" />
      </v-card-text>

      <!-- ------------------------------------------------------ the result -->
      <v-card-text v-else class="pt-4">
        <p class="kf-done">
          <LucideIcon name="circle-check" :size="18" color="#1e7a4d" class="mr-1" />
          <span>
            <strong>{{ result.campaign_id }}</strong> is live with the
            {{ selected.length }} product{{ selected.length === 1 ? "" : "s" }} you
            picked — no login needed. {{ destinationHint }}
          </span>
        </p>

        <CampaignPartnerLink :url="result.dynamic_url" />

        <!-- The pre-tracking link, kept for anyone who needs a plain page URL.
             It still works, but clicks through it are not counted and it never
             goes to the app. -->
        <div class="kf-fixed">
          <div class="kf-fixed__head">Fixed link — not tracked, website only</div>
          <v-text-field
            :value="result.url"
            label="Fixed link"
            readonly
            dense
            outlined
            hide-details
            class="kf-url"
            @focus="$event.target.select()"
          />

          <div class="kf-done__actions">
            <v-btn small outlined color="primary" class="text-none" @click="copy">
              <LucideIcon name="copy" :size="15" class="mr-1" />
              Copy fixed link
            </v-btn>
            <span v-if="copied" class="kf-copied">Copied</span>
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <template v-if="!generated">
          <v-btn text small class="text-none" :disabled="saving" @click="close">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            depressed
            small
            :loading="saving"
            :disabled="!selected.length"
            class="text-none"
            @click="generate"
          >
            {{ editing ? "Save changes" : "Generate" }}
          </v-btn>
        </template>
        <v-btn v-else color="primary" depressed small class="text-none" @click="close">
          Done
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Create a public campaign URL from a hand-picked set of products, or edit one
 * that already exists — `campaign` null means create.
 *
 * The link the admin walks away with is generated SERVER-side (see
 * products/campaign_tokens.py): it holds the marketing site's address, which is
 * a server setting, and a signed token, which only the server can mint. This
 * component never assembles a URL itself — if it did, the token could be edited
 * and the campaign the visitor lands on would no longer be the one that was
 * created.
 *
 * Editing never changes the URL, because `campaign_id` is not editable: the id
 * is inside the signed token, so issuing a new one would retire a link that may
 * already be in someone's WhatsApp. That is also why the edit path does not
 * show the "URL ready" step — there is no new link to hand over.
 */
import _ from "lodash";
import api from "@/app-js/karrofin/api";
import { copyToClipboard } from "@/app-js/karrofin/clipboard";
import CampaignPartnerLink from "@/components/karrofin/CampaignPartnerLink.vue";

const emptyForm = () => ({
  name: "",
  campaign_id: "",
  is_active: true,
  destination: "website",
});

// What each Link opens choice does, in the words the hint shows.
const DESTINATION_HINTS = {
  website: "Every visitor lands on the campaign's product page.",
  app: "Every visitor goes to the app on the Play Store; the install is tied back to the click.",
  smart: "Android phones go to the Play Store, everyone else to the product page.",
};

export default {
  name: "KfCampaignUrlDialog",
  components: { CampaignPartnerLink },
  props: {
    value: { type: Boolean, default: false },
    /**
     * The campaign to edit, or null to create a new one. Read when the dialog
     * OPENS, not watched: a background list refresh must not rewrite what
     * someone is halfway through typing. Same rule as ProductFormDialog.
     */
    campaign: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      form_valid: false,
      saving: false,
      // The row being edited, taken off the prop when the dialog opened. null
      // while creating, and every branch below reads THIS rather than the prop.
      editing: null,

      search: "",
      filter_status: null,
      filter_recommended: null,
      status_items: [
        { text: "Active", value: "active" },
        { text: "Inactive", value: "inactive" },
      ],
      recommended_items: [
        { text: "Recommended", value: "yes" },
        { text: "Not recommended", value: "no" },
      ],

      products: [],
      loading: false,
      load_error: null,
      // Ids rather than rows: the selection has to outlive a filter change,
      // and the rows behind it are replaced on every load.
      selected: [],

      generated: false,
      result: {},
      copied: false,

      destination_items: [
        { text: "Website — product page", value: "website" },
        { text: "App — Play Store", value: "app" },
        { text: "Smart — Android to Play Store, others to website", value: "smart" },
      ],

      campaign_id_rules: [
        (v) => !!v || "Campaign id is required",
        (v) =>
          /^[A-Za-z0-9_-]+$/.test(v || "") ||
          "Only letters, numbers, hyphen and underscore",
      ],
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    destinationHint() {
      return DESTINATION_HINTS[this.form.destination] || "";
    },
    dialogTitle() {
      if (this.generated) return "Campaign URL ready";
      return this.editing ? "Edit campaign URL" : "Create campaign URL";
    },
    allOnPageSelected() {
      return (
        this.products.length > 0 &&
        this.products.every((p) => this.isSelected(p.id))
      );
    },
    /** Selected products the current filters are not showing. */
    hiddenSelectedCount() {
      const visible = this.products.map((p) => p.id);
      return this.selected.filter((id) => visible.indexOf(id) === -1).length;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.fill();
    },
  },
  created() {
    this.debouncedLoad = _.debounce(this.load, 400);
  },
  beforeDestroy() {
    if (this.debouncedLoad) this.debouncedLoad.cancel();
  },
  methods: {
    fill() {
      const c = this.campaign || null;
      this.editing = c;
      this.form = emptyForm();
      if (c) {
        this.form.name = c.name || "";
        this.form.campaign_id = c.campaign_id || "";
        this.form.is_active = !!c.is_active;
        this.form.destination = c.destination || "website";
      }
      this.search = "";
      this.filter_status = null;
      this.filter_recommended = null;
      // Copied, not referenced: ticking a box must not mutate the row the list
      // is still rendering behind the dialog. `product_ids` comes off the list
      // response -- `products` there is names, which cannot tick anything.
      this.selected = c && c.product_ids ? c.product_ids.slice() : [];
      this.generated = false;
      this.result = {};
      this.copied = false;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
      this.load();
    },
    load() {
      this.loading = true;
      this.load_error = null;
      // Started off a resolved promise so a SYNCHRONOUS throw inside the call
      // becomes a rejection this chain can see. Without it the spinner runs
      // forever with nothing in the network tab -- which is exactly what
      // happens when a function is missing from api/index.js's default export
      // map and `api.x` is undefined.
      Promise.resolve()
        .then(() =>
          api.getCampaignProductOptions({
            search_query: this.search,
            status: this.filter_status,
            recommended: this.filter_recommended,
          })
        )
        .then((data) => {
          this.products = data.results;
        })
        .catch((err) => {
          this.products = [];
          this.load_error =
            this.getApiErrorMessage(err) || "Could not load products";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isSelected(id) {
      return this.selected.indexOf(id) !== -1;
    },
    toggle(id) {
      const i = this.selected.indexOf(id);
      if (i === -1) this.selected.push(id);
      else this.selected.splice(i, 1);
    },
    toggleAllOnPage() {
      const ids = this.products.map((p) => p.id);
      if (this.allOnPageSelected) {
        this.selected = this.selected.filter((id) => ids.indexOf(id) === -1);
        return;
      }
      const missing = ids.filter((id) => !this.isSelected(id));
      this.selected = this.selected.concat(missing);
    },
    generate() {
      if (!this.$refs.form.validate()) return;
      if (!this.selected.length) {
        this.showSnakeBar("error", "Select at least one product");
        return;
      }
      this.saving = true;
      const editing = this.editing;
      Promise.resolve()
        .then(() => {
          // No campaign_id in the edit payload: the id is signed into the token
          // and the server refuses to move it, so the link stays the one that
          // was shared.
          if (editing) {
            return api.updateCampaign(editing.id, {
              name: this.form.name,
              product_ids: this.selected,
              is_active: this.form.is_active,
              destination: this.form.destination,
            });
          }
          return api.createCampaign({
            name: this.form.name,
            campaign_id: this.form.campaign_id,
            product_ids: this.selected,
            destination: this.form.destination,
          });
        })
        .then((result) => {
          if (editing) {
            // No "URL ready" step -- the link did not change, so there is
            // nothing new to copy and holding the dialog open would only
            // suggest otherwise.
            this.showSnakeBar("success", "Campaign updated");
            this.$emit("saved", result);
            this.close();
            return;
          }
          this.result = result;
          this.generated = true;
          this.$emit("created", result);
        })
        .catch((err) => {
          // A duplicate campaign id is the common one, and the server names it.
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) ||
              (editing
                ? "Could not update the campaign"
                : "Could not create the campaign")
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    copy() {
      copyToClipboard(this.result.url).then((ok) => {
        this.copied = ok;
        this.showSnakeBar(
          ok ? "success" : "error",
          ok ? "Campaign URL copied" : "Could not copy — select the link and copy it"
        );
      });
    },
    close() {
      this.open = false;
    },
  },
};
</script>

<style scoped>
.kf-dialog__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--kf-brand);
}
.kf-switch >>> .v-label {
  font-size: var(--kf-fs-hint);
  color: var(--kf-text-body);
}
.kf-section {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: var(--kf-fs-label);
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 18px 0 8px;
}
.kf-section__count {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  color: var(--kf-brand);
}
.kf-picker {
  margin-top: 10px;
  border: 1px solid var(--kf-border);
  border-radius: 8px;
  max-height: 340px;
  overflow-y: auto;
}
.kf-picker__state {
  padding: 18px;
  text-align: center;
  font-size: var(--kf-fs-meta);
  color: var(--kf-text-muted);
}
.kf-picker__state--error {
  color: var(--kf-danger);
}
.kf-picker__bar {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 6px;
  background: var(--kf-surface-2);
  border-bottom: 1px solid var(--kf-border);
}
.kf-picker__note {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
}
.kf-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--kf-border);
  cursor: pointer;
}
.kf-row:last-child {
  border-bottom: none;
}
.kf-row:hover {
  background: var(--kf-hover);
}
.kf-logo {
  width: 46px;
  height: 26px;
  flex: 0 0 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--kf-surface-2);
  border-radius: 4px;
  overflow: hidden;
  font-size: 11px;
  color: var(--kf-text-muted);
}
.kf-logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.kf-row__main {
  flex: 1 1 auto;
  min-width: 0;
}
.kf-row__name {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kf-row__sub {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
}
.kf-tag {
  flex: 0 0 auto;
  font-size: var(--kf-fs-label);
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 10px;
  white-space: nowrap;
}
.kf-tag--rec {
  background: var(--kf-brand-soft);
  color: var(--kf-brand);
}
.kf-tag--on {
  background: var(--kf-success-soft);
  color: var(--kf-success);
}
.kf-tag--off {
  background: var(--kf-danger-soft);
  color: var(--kf-danger);
}
.kf-hidden-note {
  margin-top: 8px;
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
}
.kf-done {
  display: flex;
  align-items: flex-start;
  font-size: var(--kf-fs-meta);
  color: #000;
  line-height: 1.55;
  margin-bottom: 14px;
}
.kf-url >>> input {
  font-family: monospace;
  font-size: 12px;
}
.kf-done__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.kf-fixed {
  margin-top: 22px;
  padding-top: 14px;
  border-top: 1px solid var(--kf-border);
}
.kf-fixed__head {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  margin-bottom: 8px;
}
.kf-copied {
  font-size: var(--kf-fs-meta);
  font-weight: 700;
  color: var(--kf-success);
}
</style>
